<template>
    <div>
        <form-row-static label="Generelle Einstellungen">
            <div class="row">
                <div class="col-6">
                    <form-row label="Zellenabstand" class="form-row-checkbox no-margin">
                        <form-input-checkbox v-model="spacingEnabled" class="toggle"/>
                    </form-row>
                </div>
                <div class="col-6">
                    <form-input v-if="spacingEnabled" v-model="borderSpacing" placeholder="Zellenabstand"></form-input>
                </div>
            </div>
        </form-row-static>
        <hr class="no-margin-top">
        <div class="row">
            <div class="col-4">
                <form-row label="Rahmenart">
                    <form-select v-model="borderStyle" :options="borderOptions"/>
                </form-row>
            </div>
            <div class="col-4">
                <form-row label="Farbe">
                    <div class="flex">
                        <form-input :model-value="borderColor ? '#' + borderColor : 'Transparent'" readonly/>
                        <color-picker v-model="borderColor"/>
                    </div>
                </form-row>
            </div>
            <div class="col-4">
                <form-row label="Breite">
                    <form-input v-model="borderWidth" placeholder="Breite"></form-input>
                </form-row>
            </div>
            <div class="col-12">
                <div class="infobox infobox-border">
                    Bitte beachten Sie auch die Einstellungen innerhalb der Zelleneigenschaften.
                </div>
            </div>
        </div>
        <hr>
        <div class="row">
            <div class="col-4">
                <form-row label="Hintergrundfarbe">
                    <div class="flex">
                        <form-input :model-value="backgroundColor ? '#' + backgroundColor : 'Transparent'" readonly/>
                        <color-picker v-model="backgroundColor"/>
                    </div>
                </form-row>
            </div>
            <div class="col-4">
                <form-row label="Textfarbe">
                    <div class="flex">
                        <form-input :model-value="color ? '#' + color : 'Transparent'" readonly/>
                        <color-picker v-model="color"/>
                    </div>
                </form-row>
            </div>
        </div>
        <hr class="no-margin-top">
        <div class="row">
            <div class="col-8">
                <div class="row">
                    <div class="col-6">
                        <form-row label="Breite">
                            <form-input v-model="width" placeholder="Breite"></form-input>
                        </form-row>
                    </div>
                    <div class="col-6">
                        <form-row label="Höhe">
                            <form-input v-model="height" placeholder="Höhe"></form-input>
                        </form-row>
                    </div>
                </div>
            </div>
            <div class="col-4">
                <form-row label="Ausrichtung">
                    <form-select v-model="margin" :options="marginOptions"/>
                </form-row>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {usePortuna} from '@/composables/portuna';
    import ColorPicker from '@/components/ColorPicker.vue';
    import FormInput from '@/components/form/FormInput.vue';
    import FormInputCheckbox from '@/components/form/FormInputCheckbox.vue';
    import FormRow from '@/components/form/FormRow.vue';
    import FormRowStatic from '@/components/form/FormRowStatic.vue';
    import FormSelect from '@/components/form/FormSelect.vue';
    import {SubmitIcons} from '@/library/design';
    import {popupDefaultCancelButton} from '@/library/popup';
    import {toKebabCase} from '@/library/utils';
    import FroalaEditor from 'froala-editor';
    import type {TinyEmitter} from 'tiny-emitter';
    import tinycolor from 'tinycolor2';
    import {inject, ref, toRefs} from 'vue';

    const popupEmitter = inject<TinyEmitter>('popup-emitter')!;

    const portuna = usePortuna();

    const props = defineProps<{
        editor: FroalaEditor;
        tables: HTMLTableElement[];
    }>();

    const {
              editor,
              tables,
          } = toRefs(props);

    const borderOptions: SelectOption[] = [
        {
            label: 'Kein Rahmen',
            value: 0,
        },
        {
            label: 'Durchgezogen',
            value: 'solid',
        },
        {
            label: 'Gepunktet',
            value: 'dotted',
        },
        {
            label: 'Gestrichelt',
            value: 'dashed',
        },
        {
            label: 'Doppelt',
            value: 'double',
        },
        {
            label: 'Eingeritzt',
            value: 'groove',
        },
        {
            label: 'Hevorgehoben',
            value: 'ridge',
        },
        {
            label: 'Eingelassen',
            value: 'inset',
        },
        {
            label: 'Geprägt',
            value: 'outset',
        },
    ];

    const marginOptions: SelectOption[] = [
        {
            label: 'Keine',
            value: 0,
        },
        {
            label: 'Links',
            value: '0px auto 0px 0px',
        },
        {
            label: 'Zentriert',
            value: 'auto',
        },
        {
            label: 'Rechts',
            value: '0px 0px 0px auto',
        },
    ];

    const spacingEnabled  = ref(false);
    const borderSpacing   = ref('');
    const borderStyle     = ref<string | 0>(0);
    const borderColor     = ref('');
    const borderWidth     = ref('');
    const backgroundColor = ref('');
    const width           = ref('');
    const height          = ref('');
    const color           = ref('');
    const margin          = ref<string | 0>(0);

    const updateElement = () =>
    {
        let hasSelection = true;
        const selection  = editor.value.selection.get() as Selection;
        const focusNode  = selection.focusNode as Nullable<HTMLElement>;

        if (focusNode && focusNode.classList && focusNode.classList.contains('fr-element'))
        {
            hasSelection = false;
        }

        hasSelection && editor.value.selection.save();

        if (!editor.value.undo.canDo())
        {
            editor.value.undo.saveStep();
        }

        const styleRefs = {
            borderSpacing,
            borderStyle,
            borderColor,
            borderWidth,
            backgroundColor,
            width,
            height,
            color,
            margin,
        };

        for (const table of tables.value)
        {
            for (const style of [
                'borderSpacing',
                'borderStyle',
                'borderColor',
                'borderWidth',
                'backgroundColor',
                'width',
                'height',
                'color',
                'margin',
            ] as (keyof typeof styleRefs)[])
            {
                if (styleRefs[style].value)
                {
                    const isColor = ['borderColor', 'backgroundColor', 'color'].includes(style);

                    table.style.setProperty(toKebabCase(style), isColor ? '#' + styleRefs[style].value : styleRefs[style].value);
                }
                else
                {
                    table.style.removeProperty(toKebabCase(style));
                }
            }
        }

        hasSelection && editor.value.selection.restore();

        editor.value.undo.saveStep();

        popupEmitter.emit('close');
    };

    popupEmitter.emit('setButtons', [
        popupDefaultCancelButton,
        {
            title: 'Speichern',
            icon: SubmitIcons.save,
            type: 'success',
            cmd: updateElement,
        },
    ]);

    spacingEnabled.value  = !!tables.value[0].style.borderSpacing;
    borderSpacing.value   = tables.value[0].style.borderSpacing;
    borderStyle.value     = tables.value[0].style.borderStyle ? tables.value[0].style.borderStyle : 0;
    borderColor.value     = tables.value[0].style.borderColor
        ? tinycolor(tables.value[0].style.borderColor).toHex().replace('#', '')
        : tables.value[0].style.borderColor;
    borderWidth.value     = tables.value[0].style.borderWidth;
    backgroundColor.value = tables.value[0].style.backgroundColor
        ? tinycolor(tables.value[0].style.backgroundColor).toHex().replace('#', '')
        : tables.value[0].style.backgroundColor;
    width.value           = tables.value[0].style.width;
    height.value          = tables.value[0].style.height;
    color.value           = tables.value[0].style.color
        ? tinycolor(tables.value[0].style.color).toHex().replace('#', '')
        : tables.value[0].style.color;
    margin.value          = tables.value[0].style.margin ? tables.value[0].style.margin : 0;

    defineExpose(Object.assign({}, portuna, {
        componentName: 'FroalaTablePopup',
    }));
</script>